import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_URGENCES,
  dataURLtoBlob,
} from './supportConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modale de signalement de problème.
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - screenshot: string|null (dataURL PNG capturé AVANT ouverture)
 *  - capturing: bool (capture en cours)
 *  - consoleErrors: array (50 dernières erreurs console)
 *  - onSubmitted: (ticket) => void
 */
const SupportTicketModal = ({ open, onClose, screenshot, capturing, consoleErrors = [], onSubmitted }) => {
  const [categorie, setCategorie] = useState('bug_fonctionnel');
  const [urgence, setUrgence] = useState('normal');
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [showTech, setShowTech] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const metadata = useMemo(
    () => ({
      url_contexte: window.location.href,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      app_version: 'komerces-web',
      console_errors: (consoleErrors || []).slice(-50),
    }),
    [consoleErrors, open]
  );

  if (!open) return null;

  const reset = () => {
    setCategorie('bug_fonctionnel');
    setUrgence('normal');
    setDescription('');
    setIncludeScreenshot(true);
    setShowTech(false);
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Merci de décrire le problème.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('categorie', categorie);
      fd.append('urgence', urgence);
      fd.append('description', description.trim());
      fd.append('metadata', JSON.stringify(metadata));
      if (includeScreenshot && screenshot) {
        const blob = dataURLtoBlob(screenshot);
        fd.append('screenshot', blob, blob.type === 'image/png' ? 'screenshot.png' : 'screenshot.jpg');
      }
      const { data } = await axios.post(`${API}/support/tickets`, fd);
      setSuccess(true);
      if (onSubmitted) onSubmitted(data);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || 'Limite de signalements atteinte. Réessayez demain.');
      } else {
        toast.error("Échec de l'envoi du signalement. Réessayez.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4"
      data-testid="support-modal-overlay"
      onClick={handleClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        data-testid="support-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <i className="bi bi-life-preserver text-lg"></i>
            </span>
            <div>
              <h3 className="font-['Outfit'] text-base font-semibold text-slate-800">Signaler un problème</h3>
              <p className="text-xs text-slate-500">Notre équipe reçoit votre message immédiatement.</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
            title="Fermer"
            data-testid="support-modal-close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="support-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <i className="bi bi-check-lg text-3xl"></i>
            </div>
            <h4 className="font-['Outfit'] text-lg font-semibold text-slate-800">Signalement envoyé !</h4>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              Merci. Vous serez notifié par email dès qu'une réponse sera apportée.
            </p>
            <button
              onClick={handleClose}
              className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-sm transition"
              data-testid="support-success-close"
            >
              Fermer
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Catégorie */}
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
                Type de signalement
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="support-categories">
                {SUPPORT_CATEGORIES.map((c) => {
                  const active = categorie === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCategorie(c.value)}
                      data-testid={`support-cat-${c.value}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition ${
                        active
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-300'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <i className={`bi ${c.icon}`}></i>
                      <span className="truncate">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Urgence */}
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
                Urgence
              </label>
              <div className="grid grid-cols-4 gap-2" data-testid="support-urgences">
                {SUPPORT_URGENCES.map((u) => {
                  const active = urgence === u.value;
                  return (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => setUrgence(u.value)}
                      data-testid={`support-urgence-${u.value}`}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border transition ${
                        active ? u.selected : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${u.dot}`}></span>
                      {u.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Décrivez ce qui ne fonctionne pas, ce que vous attendiez, et comment le reproduire…"
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 outline-none resize-none"
                data-testid="support-description"
              />
            </div>

            {/* Capture d'écran */}
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
                Capture d'écran
              </label>
              {capturing ? (
                <div className="h-28 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-sm text-slate-400">
                  <i className="bi bi-arrow-repeat animate-spin mr-2"></i> Capture en cours…
                </div>
              ) : screenshot ? (
                <div className="space-y-2">
                  <div className="relative rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                    <img
                      src={screenshot}
                      alt="Aperçu de la capture"
                      className={`w-full max-h-44 object-contain object-top transition ${
                        includeScreenshot ? '' : 'opacity-30 grayscale'
                      }`}
                      data-testid="support-screenshot-preview"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeScreenshot}
                      onChange={(e) => setIncludeScreenshot(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/40"
                      data-testid="support-screenshot-toggle"
                    />
                    Joindre cette capture au signalement
                  </label>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Capture indisponible pour cette page.</p>
              )}
            </div>

            {/* Infos techniques (transparence) */}
            <div className="rounded-lg bg-slate-50 border border-slate-200">
              <button
                type="button"
                onClick={() => setShowTech((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-slate-500 hover:text-slate-700"
                data-testid="support-tech-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <i className="bi bi-info-circle"></i>
                  Informations techniques jointes automatiquement
                </span>
                <i className={`bi bi-chevron-${showTech ? 'up' : 'down'}`}></i>
              </button>
              {showTech && (
                <div className="px-3 pb-3 text-xs text-slate-500 space-y-1 border-t border-slate-200 pt-2">
                  <div className="truncate"><span className="text-slate-400">Page :</span> {metadata.url_contexte}</div>
                  <div><span className="text-slate-400">Écran :</span> {metadata.viewport}</div>
                  <div className="truncate"><span className="text-slate-400">Navigateur :</span> {metadata.user_agent}</div>
                  <div><span className="text-slate-400">Erreurs console :</span> {metadata.console_errors.length} capturée(s)</div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={handleClose}
                disabled={submitting}
                className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition disabled:opacity-50"
                data-testid="support-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !description.trim()}
                className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="support-submit-btn"
              >
                {submitting ? <i className="bi bi-arrow-repeat animate-spin"></i> : <i className="bi bi-send"></i>}
                Envoyer le signalement
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketModal;
